import { __ } from "@wordpress/i18n"
import { 
  useBlockProps, 
  InspectorControls,
  RichText,
  MediaUpload,
  MediaUploadCheck
} from "@wordpress/block-editor"
import { 
  PanelBody,
  Button,
  ToggleControl
} from "@wordpress/components"
import ServerSideRender from "@wordpress/server-side-render"
import { useState } from "@wordpress/element"

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({
    className: `simple-hero ${attributes.align ? `align${attributes.align}` : ''}`
  })

  const [isEditing, setIsEditing] = useState(false)

  // If we're editing, show the rich text editors instead of ServerSideRender
  if (isEditing) {
    return (
      <>
        <InspectorControls>
          <PanelBody title={__('Simple Hero Settings', 'puroclean-tw')} initialOpen={true}>
            {/* Hero Image */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {__('Hero Image:', 'puroclean-tw')}
              </label>
              <MediaUploadCheck>
                <MediaUpload
                  onSelect={(media) => setAttributes({ 
                    heroImage: {
                      url: media.url,
                      id: media.id,
                      alt: media.alt || 'Hero Image'
                    }
                  })}
                  allowedTypes={['image']}
                  value={attributes.heroImage.id}
                  render={({ open }) => (
                    <div>
                      {attributes.heroImage.url ? (
                        <div>
                          <img 
                            src={attributes.heroImage.url} 
                            alt={attributes.heroImage.alt}
                            style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                          />
                          <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                            {__('Change Image', 'puroclean-tw')}
                          </Button>
                          <Button 
                            onClick={() => setAttributes({ 
                              heroImage: { url: '', id: 0, alt: 'Hero Image' }
                            })} 
                            variant="link" 
                            isDestructive
                          >
                            {__('Remove Image', 'puroclean-tw')}
                          </Button>
                        </div>
                      ) : (
                        <Button onClick={open} variant="secondary">
                          {__('Select Hero Image', 'puroclean-tw')}
                        </Button>
                      )}
                    </div>
                  )}
                />
              </MediaUploadCheck>
            </div>
            
            {/* Flip Image Toggle */}
            <div style={{ marginBottom: '20px' }}>
              <ToggleControl
                label={__('Flip Image on Mobile', 'puroclean-tw')}
                help={__('When enabled, the image will appear between title and subtitle on mobile devices', 'puroclean-tw')}
                checked={attributes.flipImage}
                onChange={(value) => setAttributes({ flipImage: value })}
              />
            </div>
          </PanelBody>
        </InspectorControls>

        <div {...blockProps}>
          <div style={{ 
            border: '2px dashed #ddd', 
            padding: '40px 20px', 
            textAlign: 'center',
            backgroundColor: '#f9f9f9'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>
              {__('Simple Hero Section - Edit Mode', 'puroclean-tw')}
            </h3>
            
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Title:', 'puroclean-tw')}
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Enter hero title...', 'puroclean-tw')}
                  value={attributes.title}
                  onChange={(value) => setAttributes({ title: value })}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '50px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Subtitle:', 'puroclean-tw')}
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Enter hero subtitle...', 'puroclean-tw')}
                  value={attributes.subtitle}
                  onChange={(value) => setAttributes({ subtitle: value })}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '60px',
                    fontSize: '16px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Paragraph:', 'puroclean-tw')}
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Enter paragraph text (optional)...', 'puroclean-tw')}
                  value={attributes.paragraph}
                  onChange={(value) => setAttributes({ paragraph: value })}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '80px',
                    fontSize: '18px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <Button 
                  variant="primary"
                  onClick={() => setIsEditing(false)}
                  style={{ fontSize: '16px', padding: '10px 20px' }}
                >
                  {__('Done Editing - View Preview', 'puroclean-tw')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Show preview mode with ServerSideRender
  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Simple Hero Settings', 'puroclean-tw')} initialOpen={true}>
          <Button 
            variant="secondary"
            onClick={() => setIsEditing(true)}
            style={{ marginBottom: '15px', width: '100%' }}
          >
            {__('Edit Content', 'puroclean-tw')}
          </Button>
          
          {/* Hero Image */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('Hero Image:', 'puroclean-tw')}
            </label>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => setAttributes({ 
                  heroImage: {
                    url: media.url,
                    id: media.id,
                    alt: media.alt || 'Hero Image'
                  }
                })}
                allowedTypes={['image']}
                value={attributes.heroImage.id}
                render={({ open }) => (
                  <div>
                    {attributes.heroImage.url ? (
                      <div>
                        <img 
                          src={attributes.heroImage.url} 
                          alt={attributes.heroImage.alt}
                          style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                        />
                        <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                          {__('Change Image', 'puroclean-tw')}
                        </Button>
                        <Button 
                          onClick={() => setAttributes({ 
                            heroImage: { url: '', id: 0, alt: 'Hero Image' }
                          })} 
                          variant="link" 
                          isDestructive
                        >
                          {__('Remove Image', 'puroclean-tw')}
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={open} variant="secondary">
                        {__('Select Hero Image', 'puroclean-tw')}
                      </Button>
                    )}
                  </div>
                )}
              />
            </MediaUploadCheck>
          </div>
          
          {/* Flip Image Toggle */}
          <div style={{ marginBottom: '20px' }}>
            <ToggleControl
              label={__('Flip Image on Mobile', 'puroclean-tw')}
              help={__('When enabled, the image will appear between title and subtitle on mobile devices', 'puroclean-tw')}
              checked={attributes.flipImage}
              onChange={(value) => setAttributes({ flipImage: value })}
            />
          </div>
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <ServerSideRender
          block="puroclean/simple-hero"
          attributes={attributes}
          urlQueryArgs={{ context: 'edit' }}
        />
      </div>
    </>
  )
} 